import type { UIState } from './types';
import type { IntentionRecord, IntentTag } from '@retreats/content';
import { oneDayRetreat, INTENTION_CATEGORIES } from '@retreats/content';
import { loadScheduledRetreat, loadLastUsedSchedule } from '../state/schedulingState';
import { getSettingsSnapshot, getAudioReflectionEnabled } from '../state/settingsState';
import { STORAGE_KEYS, TIMING } from '../constants';
import { isE2E } from '../utils/env';

function loadIntentionRecord(): IntentionRecord {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.INTENTION_RECORD);
    if (!raw) return { kind: 'unset' };
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.kind === 'string') return parsed as IntentionRecord;
    return { kind: 'unset' };
  } catch {
    return { kind: 'unset' };
  }
}

function loadIntentLabels(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.INTENT_LABELS);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.every(x => typeof x === 'string')) return parsed;
    return [];
  } catch {
    return [];
  }
}

function loadPrimaryIntentTag(): IntentTag | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.PRIMARY_INTENT_TAG);
    if (!raw) return null;
    return INTENTION_CATEGORIES.some(c => c.id === raw) ? (raw as IntentTag) : null;
  } catch {
    return null;
  }
}

export function createInitialUIState(): UIState {
  let isFirstLaunch = true;
  let preparationSeen = false;
  let intentionAction: null | 'now' | 'scheduled' = null;
  try {
    isFirstLaunch = localStorage.getItem(STORAGE_KEYS.PERMISSIONS_GRANTED) !== 'true';
    preparationSeen = localStorage.getItem(STORAGE_KEYS.PREPARATION_SEEN) === 'true';
    if (localStorage.getItem(STORAGE_KEYS.INTENTION_PENDING) === 'true') {
      intentionAction = localStorage.getItem(STORAGE_KEYS.INTENTION_PENDING_ACTION) === 'scheduled' ? 'scheduled' : 'now';
    }
  } catch {
    isFirstLaunch = true;
  }

  const scheduled = loadScheduledRetreat();
  const now = Date.now();
  const scheduledAt = scheduled?.scheduledAt ?? null;

  const isMissed = scheduledAt !== null && scheduledAt + TIMING.MISSED_GRACE_MS < now;
  const isArmed =
    !isMissed &&
    scheduledAt !== null &&
    scheduledAt - now <= TIMING.ARMED_WINDOW_MS &&
    scheduledAt > now;

  return {
    isBooting: true,
    isFirstLaunch,
    preparationSeen,
    showEndConfirm: false,
    showCompletion: false,
    schedulingFlow: null,
    selectedSchedule: scheduled?.schedule ?? loadLastUsedSchedule() ?? oneDayRetreat,
    scheduledAt,
    hasScheduledRetreat: scheduled !== null && !isMissed,
    isArmed,
    isMissed,
    showWakeUpContainer: false,
    showTrust: false,
    showTeaching: false,
    showWakeRitual: false,
    libraryFlow: null,
    libraryInitialTradition: null,
    viewingSchedule: null,
    installFlow: null,
    showHistory: false,
    showSettings: false,
    bellFlow: null,
    previewingBellId: null,
    showReflection: false,
    intentionRecord: loadIntentionRecord(),
    intentionAction,
    intentLabels: loadIntentLabels(),
    primaryIntentTag: loadPrimaryIntentTag(),
    showEndIntention: false,
    completionElapsedSeconds: 0,
    showScheduleOverlay: false,
    isReturning: false,
    otherTabActive: false,
    otherTabRetreatName: null,
    ...getSettingsSnapshot(),
    audioReflectionEnabled: getAudioReflectionEnabled(),
    showFeedback: false,
    showAdmin: false,
    showWhatsNew: false,
    showScheduleEditor: false,
    scheduleEditorMode: "new",
    scheduleEditorSchedule: null,
    scheduleEditorBaseId: null,
    userScheduleVersion: 0,
    firstLibraryOpen: false,
    onboardingSeen: isE2E() || (() => {
      try { return localStorage.getItem(STORAGE_KEYS.ONBOARDING_SEEN) === '1'; } catch { return true; }
    })(),
    schedulingReturnSchedule: null,
    viewingTemplateId: null,
    viewingOrigin: "preset",
    viewingBaseScheduleId: null,
    scheduleEditorSaveError: false,
    pendingScheduledAt: null,
    pendingWakeOffset: null,
    pushAlarmUnconfirmed: false,
  };
}
